import { useRef } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, KeyRound, CheckCircle, XCircle } from "lucide-react";

// Import CSV Dialog
export const ImportDialog = ({
  open,
  onOpenChange,
  onImport,
  onDownloadTemplate,
  importing,
  importResult,
  setImportResult,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    await onImport(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) setImportResult(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Import Employees</DialogTitle>
          <DialogDescription>Upload a CSV file to bulk import employees</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onDownloadTemplate} data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="csv-file-input"
            />
            <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">Drag and drop or click to upload</p>
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()} 
              disabled={importing}
              data-testid="select-csv-btn"
            >
              {importing ? "Importing..." : "Select CSV File"}
            </Button>
          </div>
          {importResult && (
            <div className="space-y-2">
              <div className={`p-3 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {importResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {importResult.imported || 0} employees imported
                  </span>
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-amber-800 mb-1">Errors ({importResult.errors.length}):</p>
                  <ul className="text-xs text-amber-700 space-y-0.5">
                    {importResult.errors.map((err, i) => (
                      <li key={`import-err-${i}-${err.substring(0, 20)}`}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Export Dialog
export const ExportDialog = ({
  open,
  onOpenChange,
  format,
  setFormat,
  status,
  setStatus,
  onExport,
  exporting,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Export Employees</DialogTitle>
          <DialogDescription>Download employee data in your preferred format</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="input-field" data-testid="export-format-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV (.csv)</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Employee Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="input-field" data-testid="export-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full btn-primary" onClick={onExport} disabled={exporting} data-testid="export-employees-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create Login Dialog
export const LoginDialog = ({
  open,
  onOpenChange,
  employee,
  password,
  setPassword,
  onCreate,
  creating,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Create Employee Login</DialogTitle>
          <DialogDescription>
            Create self-service portal login for {employee?.first_name} {employee?.last_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Login Identifier:</strong> {employee?.phone}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Password *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="input-field"
              data-testid="login-password-input"
            />
          </div>
          <Button className="w-full btn-primary" onClick={onCreate} disabled={creating} data-testid="create-login-btn">
            <KeyRound className="h-4 w-4 mr-2" />
            {creating ? "Creating..." : "Create Login"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Reset Password Dialog
export const ResetPasswordDialog = ({
  open,
  onOpenChange,
  employee,
  password,
  setPassword,
  onReset,
  resetting,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Reset Employee Password</DialogTitle>
          <DialogDescription>
            Set a new password for {employee?.first_name} {employee?.last_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>New Password *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="input-field"
              data-testid="reset-password-input"
            />
          </div>
          <Button className="w-full btn-primary" onClick={onReset} disabled={resetting} data-testid="reset-password-btn">
            <KeyRound className="h-4 w-4 mr-2" />
            {resetting ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
